import * as React from "react";

/**
 * A custom hook that converts a callback to a ref to avoid triggering re-renders when passed as a
 * prop or avoid re-executing effects when passed as a dependency
 */
//eslint-disable-next-line
function useCallbackRef<T extends (...args: any[]) => any>(
    callback: T | undefined,
): T {
    const callbackRef = React.useRef(callback);

    React.useEffect(() => {
        callbackRef.current = callback;
    });

    return React.useMemo(
        () => ((...args) => callbackRef.current?.(...args)) as T,
        [],
    );
}

type UseControllableStateParams<T> = {
    prop?: T;
    defaultProp: T;
    onChange?: (state: T) => void;
};

/**
 * Custom hook that serves to invoke onChange if the value changes in the component
 * and the component is currently uncontrolled.
 *
 * @param defaultProp Default value of the state.
 * @param onChange Function to be invoked when the state changes.
 */
function useUncontrolledState<T>({
                                     defaultProp,
                                     onChange,
                                 }: Omit<UseControllableStateParams<T>, "prop">) {
    const uncontrolledState = React.useState<T>(defaultProp);
    const [value] = uncontrolledState;
    const prevValueRef = React.useRef(value);
    const handleChange = useCallbackRef(onChange);

    React.useEffect(() => {
        if (prevValueRef.current !== value) {
            handleChange(value as T);
            prevValueRef.current = value;
        }
    }, [value, prevValueRef, handleChange]);

    return uncontrolledState;
}

type SetStateFn<T> = (prevState: T) => T;

/**
 * This will delegate to a useUncontrolledState hook if the component is uncontrolled,
 * otherwise it will delegate to the specified onChange and prop value if the component is controlled.
 *
 * @param prop Value of the prop. This should be undefined if the component is uncontrolled.
 * @param defaultProp Default value of the state.
 * @param onChange Function to be invoked when the state changes.
 */
export function useControllableState<T>({
                                            prop,
                                            defaultProp,
                                            onChange = () => {},
                                        }: UseControllableStateParams<T>) {
    const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({
        defaultProp,
        onChange,
    });
    const isControlled = prop !== undefined;
    const value = isControlled ? prop : uncontrolledProp;
    const handleChange = useCallbackRef(onChange);

    const setValue: React.Dispatch<React.SetStateAction<T>> = React.useCallback(
        (nextValue) => {
            if (isControlled) {
                const setter = nextValue as SetStateFn<T>;
                const value =
                    typeof nextValue === "function" ? setter(prop) : nextValue;
                if (value !== prop) handleChange(value as T);
            } else {
                setUncontrolledProp(nextValue);
            }
        },
        [isControlled, prop, setUncontrolledProp, handleChange],
    );

    return [value, setValue] as const;
}
